import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  clearErr,
  fetchCreateOwnerClub,
  fetchReg,
} from "../../../store/authSlice";
import {
  isClinicPortal,
  getPlatformUrl,
} from "../../../utils/portalHost";
import { openRegisteredClubAdmin } from "../../../utils/authRedirect";
import { hasAdminAccess, isOwner } from "../../../utils/roles";
import AuthClubRegisterFlow from "../Auth/AuthClubRegisterFlow";
import PlatformAuthShell from "../Auth/PlatformAuthShell";
import AuthButtonLink from "../Auth/AuthButtonLink";
import Button from "../../ui/Button";
import Seo from "../../Seo";
import L from "../../../constants/baysportLabels";
import { TRIAL_DAYS } from "../../../constants/trial";
import { ownerNeedsLegal } from "../../../utils/legalAcceptance";
import a from "../Auth/auth.module.css";

function buildOwnerLoginUrl(email, nextPath = "/account") {
  const params = new URLSearchParams();
  if (email) params.set("email", email);
  if (nextPath) params.set("next", nextPath);
  const q = params.toString();
  return q ? `/account/login?${q}` : "/account/login";
}

/** После входа существующего владельца — в кабинет */
const OWNER_LOGIN_AFTER_EXISTING = "/account";

export default function ClinicRegister() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const wantNewClub = searchParams.get("new") === "1";
  const initialClubName = (searchParams.get("club") || "").slice(0, 120);
  const redirected = useRef(false);
  const registrationSubmitted = useRef(false);
  const lastRegisterEmail = useRef(null);
  const [arrivedLoggedInWithClub, setArrivedLoggedInWithClub] = useState(null);
  const [blockedEmail, setBlockedEmail] = useState(null);
  const initialized = useSelector((state) => state.init.initialized);
  const { isAuth, role, err, tenant, profile, load } = useSelector(
    (state) => state.auth,
  );

  const hasExistingClub =
    isAuth && hasAdminAccess(role) && Boolean(tenant?.subdomain);

  const ownerAddingClub = wantNewClub && isAuth && isOwner(role);

  useEffect(() => {
    if (!initialized || arrivedLoggedInWithClub !== null) return;
    setArrivedLoggedInWithClub(hasExistingClub);
  }, [initialized, hasExistingClub, arrivedLoggedInWithClub]);

  useEffect(() => {
    if (isClinicPortal()) {
      window.location.href = getPlatformUrl(
        `/registraciya${window.location.search || ""}`,
      );
    }
  }, []);

  useEffect(() => {
    if (redirected.current) return;
    if (!registrationSubmitted.current) return;
    if (load || !isAuth || !tenant?.subdomain) return;
    if (err) {
      registrationSubmitted.current = false;
      return;
    }
    redirected.current = true;
    registrationSubmitted.current = false;
    openRegisteredClubAdmin(tenant.subdomain);
  }, [isAuth, role, tenant, load, err]);

  useEffect(() => {
    if (err) {
      const t = setTimeout(() => dispatch(clearErr()), 5000);
      return () => clearTimeout(t);
    }
  }, [err, dispatch]);

  const errorPayload =
    typeof err === "string" ? { message: err } : err || null;
  const errorText = errorPayload?.message || errorPayload?.error || null;
  const ownerExists =
    errorPayload?.code === "owner_exists" ||
    (typeof errorText === "string" &&
      errorText.toLowerCase().includes("уже есть аккаунт"));

  useEffect(() => {
    if (ownerExists && lastRegisterEmail.current) {
      setBlockedEmail(lastRegisterEmail.current);
    }
  }, [ownerExists, errorText]);

  const externalExistingAccount = blockedEmail ? { email: blockedEmail } : null;

  const handleRegister = (data) => {
    lastRegisterEmail.current = data.login?.trim() || null;
    setBlockedEmail(null);
    registrationSubmitted.current = true;
    if (ownerAddingClub) {
      dispatch(fetchCreateOwnerClub(data));
    } else {
      dispatch(fetchReg(data));
    }
  };

  const handleLogin = (email) => {
    navigate(buildOwnerLoginUrl(email || "", OWNER_LOGIN_AFTER_EXISTING));
  };

  const showAlreadyHaveClub =
    initialized &&
    arrivedLoggedInWithClub === true &&
    hasExistingClub &&
    !wantNewClub;

  if (showAlreadyHaveClub) {
    return (
      <>
        <Seo
          noindex
          title={`${L.registerClub} — ${L.productName}`}
          description={`Создайте спортивный клуб и получите ${TRIAL_DAYS} дней демо-доступа: клиенты, группы, журнал и финансы.`}
        />
        <PlatformAuthShell
          backTo="/"
          backLabel="На главную"
          title="У вас уже есть клуб"
          subtitle={`Вы вошли как владелец «${tenant.name || tenant.subdomain}». Можно создать ещё один клуб.`}
        >
          <div className={a.actions}>
            <Button
              type="button"
              block
              onClick={() => openRegisteredClubAdmin(tenant.subdomain)}
            >
              Открыть админку клуба
            </Button>
            <AuthButtonLink
              to="/registraciya?new=1"
              variant="ghost"
              onClick={(e) => {
                e.preventDefault();
                navigate("/registraciya?new=1");
              }}
            >
              Зарегистрировать другой клуб
            </AuthButtonLink>
          </div>
        </PlatformAuthShell>
      </>
    );
  }

  return (
    <>
      <Seo
        noindex
        title={`${L.registerClub} — ${L.productName}`}
        description={`Создайте спортивный клуб и получите ${TRIAL_DAYS} дней демо-доступа: клиенты, группы, журнал и финансы.`}
      />
      <AuthClubRegisterFlow
        variant={ownerAddingClub ? "additional" : "full"}
        ownerEmail={profile?.email || ""}
        ownerName={profile?.full_name || profile?.fullName || ""}
        legalAlreadyAccepted={
          ownerAddingClub ? !ownerNeedsLegal(profile) : false
        }
        loading={load}
        error={errorText}
        externalExistingAccount={externalExistingAccount}
        initialClubName={initialClubName}
        onSubmit={handleRegister}
        backTo={ownerAddingClub ? "/account" : "/"}
        backLabel={ownerAddingClub ? "В кабинет" : "Назад"}
        onLogin={handleLogin}
      />
    </>
  );
}
